import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const PricingCards = ({ isStandalone = false }) => {
    const [selectedPlan, setSelectedPlan] = useState('monthly');

    // Promo code from env vars (leave empty in .env to hide promo section)
    const promoCode = process.env.REACT_APP_PROMO_CODE || '';
    const promoLabel = process.env.REACT_APP_PROMO_LABEL || 'Limited Time Offer';

    const features = [
        'Unlimited AI course generation',
        'Access to our best models',
        'Personalized learning paths',
        'Progress tracking & analytics',
        'Mobile & desktop access',
        'Priority support',
    ];

    const MonthlyCard = ({ isMobile = false }) => (
        <div
            style={{
                background: 'var(--splash-bg-card)',
                border: '1px solid var(--splash-border)',
                borderRadius: '20px',
                padding: '32px',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'pointer',
                ...(isMobile && { maxWidth: '400px', margin: '0 auto' }),
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--splash-text-tertiary)',
                letterSpacing: '0.1em',
                marginBottom: '16px',
                textTransform: 'uppercase',
            }}>
                Monthly
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
                marginBottom: '4px',
            }}>
                <span style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: 'var(--splash-text-tertiary)',
                    textDecoration: 'line-through',
                }}>$29</span>
                <span style={{
                    fontSize: '48px',
                    fontWeight: 700,
                    color: 'var(--splash-text-primary)',
                    letterSpacing: '-0.02em',
                }}>$15</span>
            </div>

            <div style={{
                fontSize: '14px',
                color: 'var(--splash-text-secondary)',
                marginBottom: '24px',
            }}>
                per month
            </div>

            <div style={{ flex: 1, marginBottom: '24px' }}>
                {features.map((feature, i) => (
                    <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '14px',
                    }}>
                        <Check size={16} style={{ color: 'var(--splash-text-secondary)', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: 'var(--splash-text-secondary)' }}>
                            {feature}
                        </span>
                    </div>
                ))}
            </div>

            <Link
                to="/login?signup=true"
                style={{
                    display: 'block',
                    width: '100%',
                    padding: '16px 24px',
                    borderRadius: '12px',
                    background: 'transparent',
                    border: '1px solid var(--splash-border)',
                    color: 'var(--splash-text-primary)',
                    fontSize: '15px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                }}
                onMouseOver={(e) => {
                    e.target.style.background = 'var(--splash-accent)';
                    e.target.style.color = 'var(--splash-bg)';
                }}
                onMouseOut={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--splash-text-primary)';
                }}
            >
                Try Graspd Free
            </Link>
        </div>
    );

    const AnnualCard = ({ isMobile = false }) => (
        <div
            style={{
                background: 'var(--splash-bg-card)',
                borderRadius: '20px',
                padding: '32px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'pointer',
                border: '1px solid var(--splash-accent)',
                ...(isMobile && { maxWidth: '400px', margin: '0 auto' }),
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div style={{
                position: 'absolute',
                top: '-12px',
                right: '24px',
                background: 'var(--splash-accent)',
                color: 'var(--splash-bg)',
                fontSize: '11px',
                fontWeight: '700',
                padding: '5px 12px',
                borderRadius: '999px',
            }}>
                SAVE 20%
            </div>

            <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--splash-accent)',
                letterSpacing: '0.1em',
                marginBottom: '16px',
                textTransform: 'uppercase',
            }}>
                Annual
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
                marginBottom: '4px',
            }}>
                <span style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: 'var(--splash-text-tertiary)',
                    textDecoration: 'line-through',
                }}>$23</span>
                <span style={{
                    fontSize: '48px',
                    fontWeight: 700,
                    color: 'var(--splash-text-primary)',
                    letterSpacing: '-0.02em',
                }}>$12</span>
            </div>

            <div style={{
                fontSize: '14px',
                color: 'var(--splash-text-secondary)',
                marginBottom: '24px',
            }}>
                per month <span style={{ color: 'var(--splash-text-primary)', fontWeight: 600 }}>($144/year)</span>
            </div>

            <div style={{ flex: 1, marginBottom: '24px' }}>
                {features.map((feature, i) => (
                    <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '14px',
                    }}>
                        <Check size={16} style={{ color: 'var(--splash-accent)', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: 'var(--splash-text-secondary)' }}>
                            {feature}
                        </span>
                    </div>
                ))}
            </div>

            <Link
                to="/login?signup=true"
                style={{
                    display: 'block',
                    width: '100%',
                    padding: '16px 24px',
                    borderRadius: '12px',
                    background: 'var(--splash-text-primary)',
                    color: 'var(--splash-bg)',
                    fontSize: '15px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                }}
                onMouseOver={(e) => {
                    e.target.style.background = 'var(--splash-accent)';
                }}
                onMouseOut={(e) => {
                    e.target.style.background = 'var(--splash-text-primary)';
                }}
            >
                Try Graspd Free
            </Link>
        </div>
    );

    return (
        <section id="pricing" style={{
            padding: isStandalone ? '100px 24px 40px' : '120px 24px',
            background: 'var(--splash-bg)',
            minHeight: isStandalone ? 'calc(100vh - 100px)' : 'auto',
            display: isStandalone ? 'flex' : 'block',
            flexDirection: 'column',
            justifyContent: isStandalone ? 'center' : 'flex-start',
        }}>
            {/* Section Header */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <h2 style={{
                    fontSize: 'clamp(32px, 5vw, 48px)',
                    fontWeight: 700,
                    color: 'var(--splash-text-primary)',
                    marginBottom: '16px',
                    letterSpacing: '-0.02em',
                    fontStyle: 'italic',
                }}>
                    Simple, Transparent Pricing
                </h2>
                <p style={{
                    fontSize: '18px',
                    color: 'var(--splash-text-secondary)',
                    maxWidth: '600px',
                    margin: '0 auto 24px',
                    lineHeight: 1.6,
                }}>
                    Lock in your rate today. Cancel anytime.
                </p>

                {/* Founders Rate + Promo Code Combined */}
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '20px',
                    background: 'var(--splash-accent-tint-10)',
                    border: '1px solid var(--splash-accent-tint-25)',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}>
                    {/* Left side - Founders Rate */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'var(--splash-accent-tint-15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--splash-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                                <path d="M12 6v6l4 2"/>
                            </svg>
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: 700,
                                color: 'var(--splash-text-primary)',
                                marginBottom: '2px',
                            }}>
                                Founders Rate · Locked for Life
                            </div>
                            <div style={{
                                fontSize: '14px',
                                color: 'var(--splash-text-secondary)',
                            }}>
                                Your price never increases. All future features included.
                            </div>
                        </div>
                    </div>

                    {/* Divider + Promo Code (only if promo exists) */}
                    {promoCode && (
                        <>
                            <div style={{
                                width: '1px',
                                height: '40px',
                                background: 'var(--splash-accent-tint-30)',
                            }} />

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}>
                                <div style={{
                                    fontSize: '13px',
                                    color: 'var(--splash-accent)',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                }}>
                                    {promoLabel}
                                </div>
                                <span style={{
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    color: 'var(--splash-text-primary)',
                                    letterSpacing: '0.05em',
                                    fontFamily: 'monospace',
                                    background: 'var(--splash-accent-tint-15)',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                }}>
                                    {promoCode}
                                </span>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(promoCode);
                                    }}
                                    style={{
                                        background: 'var(--splash-accent-tint-20)',
                                        border: '1px solid var(--splash-accent-tint-30)',
                                        borderRadius: '6px',
                                        padding: '6px 10px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        color: 'var(--splash-accent)',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.background = 'var(--splash-accent-tint-30)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.background = 'var(--splash-accent-tint-20)';
                                    }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                    Copy
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Toggle */}
            <div className="pricing-toggle" style={{
                display: 'none',
                justifyContent: 'center',
                marginTop: '32px',
                marginBottom: '24px',
            }}>
                <div style={{
                    display: 'flex',
                    background: 'var(--splash-bg-card)',
                    borderRadius: '12px',
                    padding: '4px',
                    border: '1px solid var(--splash-border)',
                }}>
                    <button
                        onClick={() => setSelectedPlan('monthly')}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            background: selectedPlan === 'monthly' ? 'var(--splash-text-primary)' : 'transparent',
                            color: selectedPlan === 'monthly' ? 'var(--splash-bg)' : 'var(--splash-text-secondary)',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontFamily: 'inherit',
                        }}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setSelectedPlan('annual')}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            background: selectedPlan === 'annual' ? 'var(--splash-text-primary)' : 'transparent',
                            color: selectedPlan === 'annual' ? 'var(--splash-bg)' : 'var(--splash-text-secondary)',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontFamily: 'inherit',
                        }}
                    >
                        Annual
                    </button>
                </div>
            </div>
            {/* Desktop: Pricing Cards Grid */}
            <div className="pricing-grid" style={{
                maxWidth: '800px',
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '24px',
                alignItems: 'start',
                marginTop: '48px',
            }}>
                <MonthlyCard />
                <AnnualCard />
            </div>

            {/* Mobile: Single Card Based on Toggle */}
            <div className="pricing-mobile" style={{ display: 'none', marginTop: '24px' }}>
                {selectedPlan === 'monthly' ? <MonthlyCard isMobile /> : <AnnualCard isMobile />}
            </div>

            <style>{`
                @media (max-width: 700px) {
                    .pricing-grid {
                        display: none !important;
                    }
                    .pricing-toggle {
                        display: flex !important;
                    }
                    .pricing-mobile {
                        display: block !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default PricingCards;
